import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from 'components/@extended/Avatar';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { getAuthUserFullName } from 'utils/auth';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import ofl_warehouse_variant3 from 'assets/images/ofl_warehouse_variant3.png';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const navigate = useNavigate();
  const fullName = useMemo(() => getAuthUserFullName() || 'OFL admin', []);
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title="Profile" disableInteractive>
        <ButtonBase
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 0.75,
            pl: 1.5,
            borderRadius: 20,
            minHeight: 60,
            '&:focus-visible': { outline: `2px solid ${theme.vars.palette.secondary.dark}`, outlineOffset: 2 }
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Typography
            variant="subtitle2"
            color="text.primary"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {fullName}
          </Typography>
          <Avatar
            alt={fullName}
            src={ofl_warehouse_variant3}
            size="sm"
            sx={{
              width: 56,
              height: 56,
              border: '2px solid #ffffff',
              boxShadow: '0 2px 8px rgba(30, 58, 95, 0.18)',
              '&:hover': { outline: '1px solid', outlineColor: 'primary.main' }
            }}
          />
        </ButtonBase>
      </Tooltip>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 9] }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={{ width: 240, p: 1.5 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography variant="subtitle1" color="text.primary" fontWeight={600} sx={{ px: 1 }}>
                    {fullName}
                  </Typography>
                  <Tooltip title="Logout">
                    <IconButton
                      size="large"
                      sx={{ color: 'text.primary' }}
                      onClick={() => {
                        handleClose({ target: null });
                        handleLogout();
                      }}
                    >
                      <LogoutOutlined />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
